import icons from '@/lib/icon';
import { DeviceDB } from '@/OptimusRoutes/types/rabbitMessage.types';
import { getDateUtcWithOffset, getDifference, parseDateToUtcFormat } from '@/utilities/dateUtils';
import { DateType } from '@/utilities/dateUtils.type';
import { distanceInMeters } from '@/utilities/util';

export type MapConfigurations = {
	idleColor: string;
	movementColor: string;
	parkingColor: string;
	traceColor: string;
	centerOnSelect?: boolean;
	centerOnUpdate?: boolean;
	traceMode?: TraceMode;
	mapType?: string;
	traffic?: boolean;
};

export enum TraceMode {
	ALL = 'ALL',
	LAST = 'LAST',
	NONE = 'NONE',
}

export enum TraceModeIntlKey {
	ALL = 'allTrace',
	LAST = 'lastTrace',
	NONE = 'noneTrace',
}

type IconProps = {
	iconId: string | number;
	color?: string;
	azimuth?: number | null;
	anchor?: google.maps.Point;
};

export type DrawFeature = {
	id: string | number;
	type: 'Feature';
	geometry: {
		type: 'Polygon';
		coordinates: number[][][];
	};
	properties: {
		mode: 'polygon' | 'rectangle' | 'circle';
		radius?: number;
		radiusKilometers?: number;
	};
};

const leftAndRightPadding = 2;
const averageWidthPerChar = 6;
const middleOfCoordinate = 2;

export const getIcon = ({ iconId, color, azimuth, anchor }: IconProps) => {
	const {
		url,
		scaledSize,
		anchor: iconAnchor,
	} = icons.svgToIcon({
		id: iconId,
		...(color && { color }),
		...(azimuth && { azimuth }),
	});

	const markerAnchor = anchor ?? iconAnchor;

	const markerIcon = {
		url,
		width: scaledSize.width,
		height: scaledSize.height,
		anchor: markerAnchor,
	};

	return markerIcon;
};

export const getLabelIcon = (label: string, backgroundColor: string, fontColor: string = 'white') => {
	const padding = 6;
	const textWidth = label.length * averageWidthPerChar + padding * leftAndRightPadding;
	const borderRadius = 6;
	const textWidthX = textWidth / 2;

	const svg = `
		<svg xmlns="http://www.w3.org/2000/svg" width="${textWidth}" height="20">
  			<rect width="${textWidth}" height="20" fill="${backgroundColor}" rx="${borderRadius}" ry="${borderRadius}" />
  			<text x="${textWidthX}" y="14" text-anchor="middle" fill="${fontColor}" font-size="10" font-family="Arial" font-weight="bold">
    			${label}
  			</text>
		</svg>`;

	const encodedSvg = encodeURIComponent(svg);
	const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodedSvg}`;

	const xCoord = (label.length * averageWidthPerChar) / middleOfCoordinate;
	const yCoord = -5;

	const markerIcon = {
		url: dataUrl,
		anchor: new google.maps.Point(xCoord, yCoord),
	};

	return markerIcon;
};

type DeviceColorData = Pick<DeviceDB, 'isOn' | 'speed' | 'utcOffsetMinutes' | 'utcDate' | 'isIdle'>;

export const isVehicleMoving = (
	isOn: boolean | null,
	speed: number | null,
	utcOffsetMinutes: number | null,
	utcDate: Date | string | null = null,
	todayUtc: DateType
) => {
	const currentUtcOffsetMinutes = utcOffsetMinutes || 0;

	const lastReportUtc = parseDateToUtcFormat(utcDate);
	const today = getDateUtcWithOffset(todayUtc, currentUtcOffsetMinutes);
	const lastReport = getDateUtcWithOffset(lastReportUtc, currentUtcOffsetMinutes);
	const timeDiff = getDifference(lastReport, today, 'minutes') <= 3;

	if (isOn && timeDiff) {
		return true;
	}
	return timeDiff && speed && speed >= 2;
};

export const getDeviceColor = (
	device: DeviceColorData,
	idleColor: string,
	movementColor: string,
	parkingColor: string
) => {
	const todayUtc = parseDateToUtcFormat();
	const vehicleMoving = isVehicleMoving(device.isOn, device.speed, device.utcOffsetMinutes, device.utcDate, todayUtc);

	if (device.isIdle) {
		return { color: idleColor, class: `car-color-${idleColor.replace('#', '')}` };
	} else if (vehicleMoving) {
		return {
			color: movementColor,
			class: `car-color-${movementColor.replace('#', '')}`,
		};
	} else {
		return {
			color: parkingColor,
			class: `car-color-${parkingColor.replace('#', '')}`,
		};
	}
};

export const getCircledAnchor = (index: number, totalEvents = 1) => {
	const radius = 20;
	const circleDegrees = 2 * Math.PI;
	const positionAngle = (index / totalEvents) * circleDegrees;
	const offsetX = radius * Math.cos(positionAngle);
	const offsetY = radius * Math.sin(positionAngle);
	const anchor = new google.maps.Point(0.5 + offsetX, 0.5 + offsetY);

	return anchor;
};

export const calculateTrailDistance = (trail: { latitude: number; longitude: number }[]) => {
	if (trail.length < 2) {
		return 0;
	}

	let totalDistance = 0;
	let lat = trail[0].latitude;
	let lon = trail[0].longitude;

	trail.forEach((point) => {
		const distance = distanceInMeters(point.latitude, point.longitude, lat, lon);
		totalDistance += distance;
		lat = point.latitude;
		lon = point.longitude;
	});

	return totalDistance / 1000;
};

export const getCircleCenter = (feature: DrawFeature): { lat: number; lng: number } | null => {
	if (!feature || !feature.geometry || feature.properties.mode !== 'circle') {
		return null;
	}

	if (!feature.geometry.coordinates || feature.geometry.coordinates.length === 0) {
		return null;
	}

	const coordinates = feature.geometry.coordinates[0];
	let sumLat = 0;
	let sumLng = 0;
	const count = coordinates.length;

	coordinates.forEach((coord) => {
		sumLng += coord[0];
		sumLat += coord[1];
	});

	const centerLat = Math.round((sumLat / count) * 1e7) / 1e7;
	const centerLng = Math.round((sumLng / count) * 1e7) / 1e7;

	return { lat: centerLat, lng: centerLng };
};

export const encodeDrawFeature = (feature: DrawFeature): string | null => {
	if (!feature || !feature.geometry) {
		return null;
	}

	if (!feature.geometry.coordinates || feature.geometry.coordinates.length === 0) {
		return null;
	}

	if (feature.properties.mode === 'circle') {
		const center = getCircleCenter(feature);

		if (!center) {
			return null;
		}

		const centerPoint = new google.maps.LatLng(center.lat, center.lng);
		return google.maps.geometry.encoding.encodePath([centerPoint]);
	}

	const path: google.maps.LatLng[] = feature.geometry.coordinates[0].map(
		(coord: number[]) => new google.maps.LatLng(coord[1], coord[0])
	);

	return google.maps.geometry.encoding.encodePath(path);
};

export const decodeDrawFeature = (
	id: string | number,
	encodedPath: string,
	mode: 'polygon' | 'rectangle' | 'circle' = 'polygon',
	radius?: number
): DrawFeature | null => {
	const decodedPath = google.maps.geometry.encoding.decodePath(encodedPath);

	if (decodedPath.length === 1 && radius) {
		const center = decodedPath[0];
		const centerLng = Math.round(center.lng() * 1e7) / 1e7;
		const centerLat = Math.round(center.lat() * 1e7) / 1e7;

		const numPoints = 64;
		const coordinates = [];

		for (let i = 0; i < numPoints; i++) {
			const angle = (i / numPoints) * 360;

			const point = google.maps.geometry.spherical.computeOffset(
				new google.maps.LatLng(centerLat, centerLng),
				radius,
				angle
			);

			coordinates.push([Math.round(point.lng() * 1e7) / 1e7, Math.round(point.lat() * 1e7) / 1e7]);
		}

		coordinates.push(coordinates[0]);

		return {
			id,
			type: 'Feature',
			geometry: {
				type: 'Polygon',
				coordinates: [coordinates],
			},
			properties: {
				mode: 'circle',
				radius,
			},
		};
	}

	const coordinates = decodedPath.map((latLng) => [
		Math.round(latLng.lng() * 1e7) / 1e7,
		Math.round(latLng.lat() * 1e7) / 1e7,
	]);

	const firstCoord = coordinates[0];
	const lastCoord = coordinates[coordinates.length - 1];

	if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
		coordinates.push([firstCoord[0], firstCoord[1]]);
	}

	return {
		id,
		type: 'Feature',
		geometry: {
			type: 'Polygon',
			coordinates: [coordinates],
		},
		properties: {
			mode: mode,
		},
	};
};
